import React, { useEffect } from 'react'
import { number } from 'yup'
import { ScaleExplanation } from '../../atoms/ScaleExplanation'
import { isNoneNull } from '../../utils/isNoneNull'
import { IIntakeStep } from './IIntakeStep'
import { IntakeStepButton } from './IntakeStepButton'
import { IntakeStepChecklist } from './IntakeStepChecklist'
import { IntakeStepInputDate } from './IntakeStepInputDate'
import { IntakeStepInputText } from './IntakeStepInputText'
import { IntakeStepRadioButtons } from './IntakeStepRadioButtons'
import { IntakeStepRadioButtonsScale } from './IntakeStepRadioButtonsScale'
import { IntakeStepScale } from './IntakeStepScale'

const lang = process.env.REACT_APP_LANGUAGE === 'es' ? 'es' : 'en'

const translations = {
  'en': {
    childFirstName: 'First name of child',
    childFirstNamePlaceholder: 'Enter first name',
    childLastName: 'Last name of child',
    childLastNamePlaceholder: 'Enter last name',
    childNickname: 'Nickname of child',
    childNicknamePlaceholder: 'Enter nickname',
    childDateOfBirth: 'Date of Birth',
    childDateOfBirthPlaceholder: 'Enter date of birth',
    childGender: 'Gender',
    male: 'Male',
    female: 'Female',
    yes: 'Yes',
    no: 'No',
    veryPoorly: 'Very poorly',
    veryWell: 'Very well',
    childHadGeneralAnesthesia: 'Has your child ever undergone general anesthesia?',
    scaleIfFalse: 'Please click below to show how well your child handled their last medical encounter (i. e. pediatrician visit, dental appointment, etc.)',
    scaleIfTrue: 'Please click below to show how well your child handled that experience',
    okLetsGo: 'OK, LET\'S GO',
    checkedChildReactionsText: 'We would like you to tell us what your child’s reaction is likely to be in those situations. Please read\n' +
      '        each statement and decide whether it is a "true" or "untrue" description of your child’s reaction within\n' +
      '        the past six months. Use the following scale to indicate how well a statement describes your child.',
    nextItemsInstructions: 'The next items are statements that describe children’s reactions to a number of situations.',
    childAfraidOfBurglers: 'Is afraid of burglars or the "boogie man',
    childAfraidOfLoudNoises: 'Is afraid of loud noises',
    childNotAfraidOfTheDark: 'Is not afraid of the dark',
    childAfraidOfFire: 'Is afraid of fire',
    childAfraidOfTheDark: 'Is afraid of the dark',
    childRarelyAfraidMonsters: 'Is rarely frightened by "monsters" seen on TV or at movies',
    forParentPrimarily: 'For parent primarily involved with preparation before and on the day of surgery.',
    goToParentInfo: 'Go to Parent information',
    howOld: 'How old are you?',
    parent: 'Parent',
    mother: 'Mother',
    father: 'Father',
    generallyFeel: 'Please read each statement and choose the response that indicates how you generally feel. Do not spend too\n' +
      '        much time on any one statement but give the answer which seems to describe how you generally feel.',
    guardian: 'Guardian',
    parentAnxiousness: 'How anxious do you think you will feel when your child is put to sleep for surgery?',
    veryAnxious: 'Very anxious',
    notAnxious: 'Not anxious',
    parentFeelPleasant: 'I feel pleasant',
    parentAmCalmCoolCollected: 'I am "calm, cool, and collected',
    parentDifficultiesNotOvercome: 'I feel that difficulties are piling up so that I cannot overcome them',
    parentWorryTooMuch: 'I worry too much over something that doesn\'t really matter',
    parentAmHappy: 'I am happy',
    parentTakeThingsHard: 'I am inclined to take things hard',
    parentAmContent: '',
    parentFeelBlue: 'I feel blue',
    parentTakeDisappointmentsTooKeenly: 'I take disappointments so keenly that I can\'t put them out of my mind',
    parentFeelCrying: '',
    parentFeelRested: '',
    almostNever: 'Almost\nNever',
    sometimes: 'Sometimes',
    often: 'Oten',
    almostAlways: 'Almost \n Always',
    parentSteadyPerson: 'I am a steady person',
    parentFeelSecure: 'I feel secure',
    parentShareFeelings: 'To be able to openly share your feelings.',
    parentRespectOthers: 'To show respect for others.',
    parentAvoidConflict: 'To avoid conflict at all costs.',
    parentControlEmotions: 'To control your emotions.',
    parentShowLoyalty: 'To show loyalty.',
    parentObeyWishes: 'To obey or fulfill others\' wishes or requests.',
    parentShowAgreement: 'To show agreement with opinions that are different from your own.',
    parentGoodManners: 'To show good manners and be polite no matter what.',
    parentFeelComfortable: 'To make others feel comfortable.',
    parentAvoidRudeness: 'To avoid being rude or insulting.',
    childFavoriteToys: 'What are some of your child\'s favorite toys?',
    childFavoriteGames: 'What are your child\'s favorite games?',
    childEnjoysMovies: 'What movies does your child enjoy or has seen recently?',
    childPets: 'What types of pets does your child have, if any?',
    childSportsOrExtracurricular: 'Does your child play any sport or extracurricular activities? If so, which ones?',
    notImportant: 'Not Important',
    sympaticaInstructions: 'When interacting with other people, how important is it for you:',
    religiosityInstructions: '',
    aLittleImportant: 'A Little Important',
    next: 'NEXT',
    somewhatImportant: 'Somewhat Important',
    veryImportant: 'Very Important',
    extremelyImportant: 'Extremely Important',
    extremelyUntrue: 'Extremely\nuntrue',
    quiteUntrue: 'Quite\nuntrue',
    slightlyUntrue: 'Slightly\nuntrue',
    neitherTrueNorUntrue: 'Neither true\nnor untrue',
    slightlyTrue: 'Slightly\ntrue',
    quiteTrue: 'Quite\ntrue',
    extremelyTrue: 'Extremely\ntrue',
    familyDescription: 'Please use the scale below to indicate how much each statement below describes you',
    veryMuchDisagreement: 'Very much in Disagreement',
    veryMuchInAgreement: 'Very Much in Agreement',
    aLittleDisagreement: 'Slightly Disagree',
    notDisagreement: 'Neither agree nor disagree',
    aLittleAgree: 'Agree',

    parentMakeSacrifices: 'One should make great sacrifices in order to guarantee a good education for his/her children.',
    parentHasProblems: 'When one has problems, one can count on the help of relatives.',
    wouldHelpRelatives: ' I would help within my means if a relative told me that she/he is in financial difficulty.',
    shouldLiveLong: 'One should have the hope of living long enough to see his/her grandchildren grow up.',
    agingParents: 'Aging parents should live with their relatives.',
    shouldShareWithRelatives: 'A person should share his/her home with uncles, aunts, or first cousins if they are in need.',
    helpRelatives: 'When someone has problems s/he can count on help from his/her relatives.',
    helpEconomically: 'One should help economically with the support of younger brothers and sisters.',
    shouldBeEmbarrassed: 'One should be embarrassed about the bad things done by his/her brothers or sisters.',
    shouldPleaseParents: 'Much of what a son or daughter does should be done to please the parents.',
    familyShouldConsult: 'The family should consult close relatives (uncles, aunts) concerning its important decisions.',
    canCountOnRelative: 'One can count on help from his/her relatives to solve most problems',
    shouldLiveInParentsHouse: 'Children should live in their parents’ house until they get married.',
    goalToHaveChildren: 'One of the most important goals in life is to have children.',
    markAllLabel: 'Please read the following scenarios and MARK ALL appropriate answers',
    dentistDescription: `Vividly imagine that you are afraid of the dentist and have to get some dental work done.  Which of the following would you do? Please mark all of the statements that might apply to you.`,
    dentistAnswer1: 'I would ask the dentist exactly what was going to be done.',
    dentistAnswer2: 'I would take a tranquilizer or have a drink before going.',
    dentistAnswer3: 'I would try to think about pleasant memories.',
    dentistAnswer4: 'I would want the dentist to tell me when I would feel pain.',
    dentistAnswer5: 'I would try to sleep.',
    dentistAnswer6: 'I would watch all the dentist\'s movements and listen for the sounds of the drill.',
    dentistAnswer7: 'I would watch the flow of water from my mouth to see if it contained blood.',
    dentistAnswer8: 'I would do mental puzzles in my mind.',
    airplaneDescription: `Vividly imagine that you are on an airplane thirty minutes from your destination, when the plane unexpectedly goes into a deep dive and then suddenly levels off.  After a short time, the pilot announces that nothing is wrong, although the rest of the ride may be rough.  You, however, are not convinced that all is well.  Please check all of the statements that might apply to you.`,
    airplaneAnswer1: `I would carefully read the information provided about the safety features in the plane and make sure I knew where the emergency exits were.`,
    airplaneAnswer2: 'I would make small talk with the passenger beside me.',
    airplaneAnswer3: 'I would watch the end of a movie, even if I had seen it before.',
    airplaneAnswer4: 'I would call for the flight attendant and ask exactly what the problem was.',
    airplaneAnswer5: 'I would order a drink or tranquilizer from the flight attendant.',
    airplaneAnswer6: `I would listen carefully to the engines for unusual noises and would watch the crew to see if their behavior was out of the ordinary.`,
    airplaneAnswer7: 'I would talk to the passenger beside me about what might be wrong.',
    airplaneAnswer8: 'I would settle down and read a book or magazine or write a letter.',
    religiousDescription: 'Religious Belief Coping Scale',
    religiousScale1: 'Not at all',
    religiousScale2: 'A bit',
    religiousScale3: 'A lot',
    question18: 'A number of statements which people have used to describe themselves are given below.',
    religousQ1: 'Tried to see how God might be trying to strengthen me in this situation ',
    religousQ2: 'Tried to put my plans into action together with God ',
    religousQ3: 'Sought help from God in letting go of my anger ',
    religousQ4: 'Sought God’s love and care',
    religousQ5: 'Asked forgiveness for my sins ',
    religousQ6: 'Focused on religion to stop worrying about my problems',
    religousQ7: 'Looked for a stronger connection with God',
  },
  'es': {
    childFirstName: 'Nombre',
    childFirstNamePlaceholder: 'Nombre',
    childLastName: 'Apellido',
    childLastNamePlaceholder: 'Apellido',
    childNickname: 'Apodo del niño',
    childNicknamePlaceholder: 'Apodo del niño',
    childDateOfBirth: 'Fecha de nacimiento',
    childDateOfBirthPlaceholder: 'Fecha de nacimiento',
    childGender: 'Género del niño',
    male: 'Masculino',
    female: 'Femenino',
    yes: 'Si',
    next: 'próximo',
    no: 'No',
    veryPoorly: 'muy mal',
    veryWell: 'muy bien',
    almostNever: 'Casi Nunca',
    sometimes: 'A veces',
    often: 'Frecuentemente',
    almostAlways: 'Casi siempre',
    generallyFeel: 'Lea cada afirmación y elija la respuesta que indique cómo se siente en general. No gastes \n' +
      ' mucho tiempo en cualquier declaración, pero da la respuesta que parece describir cómo te sientes en general.',
    childHadGeneralAnesthesia: '¿Su hijo ha sido sometido alguna vez a anestesia general?',
    scaleIfFalse: 'Si su hijo no se ha sometido nunca a una anestesia general, seleccione un número en la escala de valoración que aparece a continuación para mostrar lo bien que ha manejado su hijo su último encuentro médico:',
    scaleIfTrue: 'Si su hijo ha sido sometido a anestesia general, por favor, seleccione un número en la escala de valoración de abajo para mostrar lo bien que su hijo manejó esa experiencia:',
    okLetsGo: 'OK, Vamos',
    checkedChildReactionsText: 'Nos gustaría que nos dijera cuál es la reacción probable de su hijo en esas situaciones. Lea cada declaración y decida si es una descripción "cierta" o "falsa" de la reacción de su hijo en los últimos seis meses. Use la siguiente escala para indicar qué tan bien una declaración describe a su hijo.',
    nextItemsInstructions: 'Los siguientes elementos son declaraciones que describen las reacciones de los niños ante una serie de situaciones.',
    childAfraidOfBurglers: 'Mi hijo les teme a los ladrones o al "Cucuy".',
    childAfraidOfLoudNoises: 'Mi hijo tiene miedo a los ruidos fuertes.',
    childNotAfraidOfTheDark: 'Mi hijo no tiene miedo a la oscuridad.',
    childAfraidOfFire: 'Mi hijo tiene miedo al fuego.',
    childAfraidOfTheDark: 'Mi hijo tiene miedo a la oscuridad.',
    childRarelyAfraidMonsters: 'Mi hijo rara vez se asusta con los"monstruos" que se ven en la televisión o en películas.',
    forParentPrimarily: 'Para padres principalmente involucrados en la preparación antes y el día de la cirugía.',
    goToParentInfo: 'Vamos a información para padres',
    howOld: '¿Qué edad tiene usted?',
    parent: '¿Cuál es su relación con el niño?',
    mother: 'Madre',
    father: 'Padre',
    guardian: 'Otro Guardián',
    parentAnxiousness: '¿Cuánta ansiedad cree que sentirá cuando su hijo sea dormido para la cirugía?',
    veryAnxious: 'Muy Ansioso',
    notAnxious: 'No Ansioso',
    parentFeelPleasant: 'Me siento bien',
    parentAmCalmCoolCollected: 'Me siento sereno/a, calmado/a, tranquilo/a',
    parentDifficultiesNotOvercome: 'Siento que las dificultades se acumulan de tal forma que no puedo superarlas',
    parentWorryTooMuch: 'Me preocupo mucho por cosas que no son realmente importantes',
    parentAmHappy: 'Me siento alegre',
    parentTakeThingsHard: 'Las cosas tienden a afectarme mucho',
    parentAmContent: 'Estoy contento/a',
    parentFeelBlue: 'Me siento melancólico/a',
    parentTakeDisappointmentsTooKeenly: 'Las frustraciones me afectan tan intensamente que no puedo sacarlas de mi mente',
    parentFeelCrying: '',
    parentFeelRested: '',
    parentSteadyPerson: 'Soy una persona estable.',
    parentFeelSecure: 'Me siento seguro/a',
    parentShareFeelings: 'Poder compartir abiertamente sus sentimientos.',
    parentRespectOthers: 'Mostrar respeto a otras personas.',
    parentAvoidConflict: 'Evitar conflicto a toda costa.',
    parentControlEmotions: 'Tener control de sus emociones.',
    parentShowLoyalty: 'Mostrar lealtad.',
    parentObeyWishes: 'Obedecer o satisfacer los deseos  de otra persona.',
    parentShowAgreement: 'Demostrar acuerdo con  opiniones que sean diferentes que las suyas.',
    parentGoodManners: 'Demostrar buenos modales y ser cortés no importe la situación.',
    parentFeelComfortable: 'Hacer sentir a gusto a otros.',
    parentAvoidRudeness: 'Evitar ser irrespetuoso y evitar  insultar a alguien.',
    childFavoriteToys: '¿Cuáles son los juguetes favoritos de su hijo?',
    childFavoriteGames: '¿Cuáles son los juegos favoritos de su hijo?',
    childEnjoysMovies: '¿Qué películas le gustan a su hijo o ha visto recientemente?',
    childPets: '¿Qué tipo de mascotas tiene su hijo, si es que tiene alguna?',
    childSportsOrExtracurricular: '¿Practica su hijo algún deporte o actividad extraescolar? En caso afirmativo, ¿cuáles?',
    notImportant: 'No Importante',
    sympaticaInstructions: 'En sus interacciones con otras personas ¿qué tan importante es para usted?:',
    aLittleImportant: 'Un poco Importante',
    somewhatImportant: 'Algo Importante',
    veryImportant: 'Muy Importante',
    extremelyImportant: 'Extremadamente Importante',
    extremelyUntrue: 'Extremadamente falso',
    quiteUntrue: 'Bastante falso',
    slightlyUntrue: 'Ligeramente falso',
    neitherTrueNorUntrue: 'Ni cierto ni falso',
    slightlyTrue: 'Ligeramente cierto',
    quiteTrue: 'Bastante cierto',
    extremelyTrue: 'Extremadamente cierto',
    familyDescription: 'Utilice la siguiente escala para indicar en qué medida le describe cada una de las siguientes afirmaciones.',
    veryMuchDisagreement: 'Completamente en desacuerdo',
    veryMuchInAgreement: 'Completamente en acuerdo',
    aLittleDisagreement: 'En desacuerdo',
    notDisagreement: 'No en desacuerdo ni en acuerdo',
    aLittleAgree: 'En acuerdo',
    parentMakeSacrifices: 'Uno debe hacer grandes sacrificios para garantizar una buena educación para sus hijos.',
    parentHasProblems: 'Cuando uno tiene problemas, puede contar con la ayuda de los familiares.',
    wouldHelpRelatives: 'Yo ayudaría dentro de mis medios si un familiar me dijera que el/ella está en una dificultad financiera',
    shouldLiveLong: 'Uno debe tener la esperanza de vivir lo suficiente para ver a sus nietos crecer.',
    agingParents: 'Padres/madres más viejos deben vivir con sus familiares.',
    shouldShareWithRelatives: 'Una persona debe compartir su casa con tíos, tías, o primos cercanos si ellos están necesitados.',
    helpRelatives: 'Cuando uno tiene problemas, uno puede contar con la ayuda de sus familiares.',
    helpEconomically: 'Uno debe ayudar económicamente con el mantenimiento de hermanos más jóvenes.',
    shouldBeEmbarrassed: 'Uno debe de estar avergonzado acerca de cosas malas hechas por sus hermanos.',
    shouldPleaseParents: 'Mucho de lo que un hijo hace debe ser hecho para satisfacer a sus padres.',
    familyShouldConsult: 'La familia debe consultar familiares (tíos, tías) sobre sus decisiones importantes.',
    canCountOnRelative: ' Uno puede contar con la ayuda de sus familiares para resolver la mayoría de los problemas',
    shouldLiveInParentsHouse: 'Los hijos deben vivir en la casa de sus padres hasta que se casen.',
    goalToHaveChildren: 'Una de las más importantes metas en la vida es tener hijos.',
    markAllLabel: 'Por favor lea los siguientes escenarios y MARQUE TODAS las respuestas apropiadas',
    dentistDescription: `Imagine que tiene miedo al dentista y que tiene que ir para someterse a algún tipo de intervención dental ¿Qué es la que haría usted? Escoja todas aquellas cosas que haría usted en tal caso.`,
    dentistAnswer1: 'Preguntaría al dentista qué es lo que tiene intención de hacer, exactamente',
    dentistAnswer2: 'Tomaría un tranquilizante o una copa antes de ir',
    dentistAnswer3: 'Intentaría pensar en recuerdos agradables',
    dentistAnswer4: 'Pediría al dentista que me dijera cuándo me iba a doler',
    dentistAnswer5: 'Intentaría dormir',
    dentistAnswer6: 'Me fijaría en todos los movimientos del dentista y estaría atento al sonido de su taladro',
    dentistAnswer7: 'Observaría el flujo del agua que expulsara de mi boca para ver si contenía sangre',
    dentistAnswer8: 'Haría rompecabezas mentalmente',
    airplaneDescription: `Imagine que está en un avión, cuando, 30 minutos antes de llegar a su destino, cae en picado inesperadamente y que después, de repente, endereza su rumbo. Tras unos breves momentos, el piloto anuncia que todo está bien pero que el resto del viaje puede ser escabroso. Usted, sin embargo, no está convencido de que todo vaya bien. ¿Qué es lo que haría usted? Escoja todas aquellas cosas que haría usted en tal caso.`,
    airplaneAnswer1: `Leería cuidadosamente la información sobre las características de seguridad del avión y me aseguraría que sabía dónde estaban las salidas de emergencia.`,
    airplaneAnswer2: 'Haría una breve conversación con el pasajero de al lado.',
    airplaneAnswer3: 'Miraría la película hasta el final, aunque la hubiera visto anteriormente.',
    airplaneAnswer4: 'Llamaría a la azafata y le pediría que me dijera cual era el problema, exactamente',
    airplaneAnswer5: 'Pediría una copa o un tranquilizante a la azafata.',
    airplaneAnswer6: `Escucharía atentamente los motores por si hacían ruidos inusuales y vigilaría la tripulación para comprobar si comportamiento estaba fuera de lo normal.`,
    airplaneAnswer7: 'Hablaría con el pasajero de al lado sobre que podía andar mal.',
    airplaneAnswer8: 'Me calmaría y leería un libro o revista o escribiría una carta. ',
    religiousDescription: 'Escala breve de afrontamiento religioso',
    religiousScale1: 'Para nada',
    religiousScale2: 'A veces',
    religiosityInstructions: 'Piense en el evento más estresante que ha tenido en el año pasado. Luego, lea cada estrategia religiosa e indique el grado en que se realizó cada una de ellas.',
    religiousScale3: 'Mucho',
    question18: 'A continuación se dan varias afirmaciones que las personas han usado para describirse a sí mismas.',
    religousQ1: 'Intento creer que Dios me fortalece a través de esta situación.',
    religousQ2: 'Junto con Dios, traté de llevar a cabo mis planes.',
    religousQ3: 'Busqué la ayuda de Dios para poder olvidar mi enojo.',
    religousQ4: 'Busqué el amor y el cuidado de Dios.',
    religousQ5: 'Pedí perdón por mis pecados.',
    religousQ6: ' Me enfoqué en la religión para dejar de preocuparme por mis problemas.',
    religousQ7: 'Busqué una cercanía más fuerte con Dios.',
  },
}

const steps: IIntakeStep[] = [
  /*{
    Component: IntakeStepInputText, completed: intake => !!intake.childFirstName,
    label: `${translations[lang].childFirstName}`, name: 'childFirstName', placeholder: `${translations[lang].childFirstNamePlaceholder}`,
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childLastName,
    label: `${translations[lang].childLastName}`, name: 'childLastName', placeholder: `${translations[lang].childLastNamePlaceholder}`,
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childNickname,
    label: `${translations[lang].childNickname}`, name: 'childNickname', placeholder: `${translations[lang].childNicknamePlaceholder}`,
  },
  {
    Component: IntakeStepInputDate, completed: intake => !!intake.childDateOfBirth,
    label: `${translations[lang].childDateOfBirth}`, name: 'childDateOfBirth', placeholder: `${translations[lang].childDateOfBirthPlaceholder}`,
  },*/
  {
    Component: IntakeStepRadioButtons, completed: intake => !!intake.childGender,
    label: `${translations[lang].childGender}`, name: 'childGender',
    options: [{ label: `${translations[lang].male}`, value: 'male' }, { label: `${translations[lang].female}`, value: 'female' }],
  },
  {
    Component: IntakeStepRadioButtonsScale, completed:
      intake => (intake.childHadGeneralAnesthesia === false && intake.childHandledLastMedicalEncounter !== null) ||
        (intake.childHadGeneralAnesthesia === true && intake.childHandledGeneralAnesthesia !== null),
    label: `${translations[lang].childHadGeneralAnesthesia}`, name: 'childHadGeneralAnesthesia',
    options: [{ label: `${translations[lang].yes}`, value: true }, { label: `${translations[lang].no}`, value: false }],
    scaleIfFalse: {
      from: 0, fromLabel: `${translations[lang].veryPoorly}`, label: `${translations[lang].scaleIfFalse}`,
      name: 'childHandledLastMedicalEncounter', to: 10, toLabel: `${translations[lang].veryWell}`,
    },
    scaleIfTrue: {
      from: 0, fromLabel: `${translations[lang].veryPoorly}`,
      label: `${translations[lang].scaleIfTrue}`,
      name: 'childHandledGeneralAnesthesia', to: 10, toLabel: `${translations[lang].veryWell}`,
    },
  },
  {
    Component: IntakeStepButton, buttonLabel: `${translations[lang].okLetsGo}`, children: <div>
      {translations[lang].checkedChildReactionsText}
      <ScaleExplanation labels={[
        `${translations[lang].extremelyUntrue}`,
        `${translations[lang].quiteUntrue}`,
        `${translations[lang].slightlyUntrue}`,
        `${translations[lang].neitherTrueNorUntrue}`,
        `${translations[lang].slightlyTrue}`,
        `${translations[lang].quiteTrue}`,
        `${translations[lang].extremelyTrue}`,
      ]} />
    </div>, completed: intake => !!intake.checkedChildReactionsText,
    label: translations[lang].nextItemsInstructions,
    name: 'checkedChildReactionsText',
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childAfraidOfBurglars !== null,
    label: `${translations[lang].childAfraidOfBurglers}`, name: 'childAfraidOfBurglars', scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childAfraidOfLoudNoises !== null,
    label: `${translations[lang].childAfraidOfLoudNoises}`, name: 'childAfraidOfLoudNoises', scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childNotAfraidOfTheDark !== null,
    label: `${translations[lang].childNotAfraidOfTheDark}`, name: 'childNotAfraidOfTheDark', scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childAfraidOfFire !== null,
    label: `${translations[lang].childAfraidOfFire}`, name: 'childAfraidOfFire', scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childAfraidOfTheDark !== null,
    label: `${translations[lang].childAfraidOfTheDark}`, name: 'childAfraidOfTheDark', scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.childRarelyAfraidMonsters !== null,
    label: `${translations[lang].childRarelyAfraidMonsters}`, name: 'childRarelyAfraidMonsters',
    scaleFrom: 1, scaleTo: 7,
  },
  {
    Component: IntakeStepButton, buttonLabel: translations[lang].next, children:
      translations[lang].forParentPrimarily, completed: intake => !!intake.checkedParentInfoText,
    label: translations[lang].goToParentInfo,
    name: 'checkedParentInfoText',
  },

  // PARENT INFORMATION
  {
    Component: IntakeStepInputText, completed: intake => !!intake.parentAge,
    label: `${translations[lang].howOld}`, name: 'parentAge', placeholder: lang === 'es' ? 'ingrese la edad' : 'Enter age',
    strToVal: (s: string) => parseInt(s, 10), valToStr: (v: number) => v === null ? '' : String(v),
    validationError: (s: any) => number().integer().min(12).max(120).isValidSync(parseFloat(s)) ? null :
      lang === 'es' ? 'Por favor ingrese una edad válida' : 'Please enter a valid age',
  },
  {
    Component: IntakeStepRadioButtons, completed: intake => !!intake.parentRole,
    label: `${translations[lang].parent}`, name: 'parentRole',
    options: [{ label: `${translations[lang].mother}`, value: 'mother' }, { label: `${translations[lang].father}`, value: 'father' }],
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAnxiousness !== null,
    label: `${translations[lang].parentAnxiousness}`,
    name: 'parentAnxiousness', scaleFrom: 0, scaleTo: 10,
    fromLabel: `${translations[lang].notAnxious}`,
    toLabel: `${translations[lang].veryAnxious}`,

  },
  {
    Component: IntakeStepButton, buttonLabel: `${translations[lang].okLetsGo}`, children: <div>
      <div> {translations[lang].generallyFeel} </div>
      <ScaleExplanation labels={[ translations[lang].almostNever, translations[lang].sometimes, translations[lang].often, translations[lang].almostAlways ]} />
    </div>, completed: intake => !!intake.checkedParentDescribeText,
    label: `${translations[lang].question18}`,
    name: 'checkedParentDescribeText',
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentFeelPleasant !== null,
    label: `${translations[lang].parentFeelPleasant}`,
    name: 'parentFeelPleasant', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAmCalmCoolCollected !== null,
    label: `${translations[lang].parentAmCalmCoolCollected}`,
    name: 'parentAmCalmCoolCollected', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentDifficultiesNotOvercome !== null,
    label: `${translations[lang].parentDifficultiesNotOvercome}`,
    name: 'parentDifficultiesNotOvercome', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentWorryTooMuch !== null,
    label: `${translations[lang].parentWorryTooMuch}`,
    name: 'parentWorryTooMuch', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAmHappy !== null,
    label: `${translations[lang].parentAmHappy}`,
    name: 'parentAmHappy', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentTakeThingsHard !== null,
    label: `${translations[lang].parentTakeThingsHard}`,
    name: 'parentTakeThingsHard', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentFeelSecure !== null,
    label: `${translations[lang].parentFeelSecure}`,
    name: 'parentFeelSecure', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentFeelBlue !== null,
    label: `${translations[lang].parentFeelBlue}`,
    name: 'parentFeelBlue', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAmContent !== null,
    label: `${translations[lang].parentAmContent}`,
    name: 'parentAmContent', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentTakeDisappointmentsTooKeenly !== null,
    label: `${translations[lang].parentTakeDisappointmentsTooKeenly}`,
    name: 'parentTakeDisappointmentsTooKeenly', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentSteadyPerson !== null,
    label: `${translations[lang].parentSteadyPerson}`,
    name: 'parentSteadyPerson', scaleFrom: 1, scaleTo: 4,
  },
  {
    Component: IntakeStepChecklist, completed: intake => isNoneNull(
      intake.parentDentistAsk,
      intake.parentDentistTranquilizerOrDrink,
      intake.parentDentistThinkPleasant,
      intake.parentDentistTellMeAboutPain,
      intake.parentDentistTrySleep,
      intake.parentDentistWatchMovementsAndListenDrill,
      intake.parentDentistWatchForBlood,
      intake.parentDentistMentalPuzzles,
    ) ,
    description: `${translations[lang].dentistDescription}`,
    label: `${translations[lang].markAllLabel}`,
    options: [
      { label: `${translations[lang].dentistAnswer1}`, name: 'parentDentistAsk' },
      { label: `${translations[lang].dentistAnswer2}`, name: 'parentDentistTranquilizerOrDrink' },
      { label: `${translations[lang].dentistAnswer3}`, name: 'parentDentistThinkPleasant' },
      { label: `${translations[lang].dentistAnswer4}`, name: 'parentDentistTellMeAboutPain' },
      { label: `${translations[lang].dentistAnswer5}`, name: 'parentDentistTrySleep' },
      { label: `${translations[lang].dentistAnswer6}`,
        name: 'parentDentistWatchMovementsAndListenDrill' },
      { label: `${translations[lang].dentistAnswer7}`,
        name: 'parentDentistWatchForBlood' },
      { label: `${translations[lang].dentistAnswer8}`, name: 'parentDentistMentalPuzzles' },
    ],
  },
  {
    Component: IntakeStepChecklist, completed: intake => isNoneNull(
      intake.parentPlaneReadEmergency,
      intake.parentPlaneSmallTalk,
      intake.parentPlaneWatchMovie,
      intake.parentPlaneAskFlightAttendant,
      intake.parentPlaneTranquilizerOrDrink,
      intake.parentPlaneListenEnginesAndCrew,
      intake.parentPlaneTalkAboutIssue,
      intake.parentPlaneReadOrWrite,
    ) ,
    description: `${translations[lang].airplaneDescription}`,
    label: `${translations[lang].markAllLabel}`,
    options: [
      { label: `${translations[lang].airplaneAnswer1}`, name: 'parentPlaneReadEmergency' },
      { label: `${translations[lang].airplaneAnswer2}`, name: 'parentPlaneSmallTalk' },
      { label: `${translations[lang].airplaneAnswer3}`, name: 'parentPlaneWatchMovie' },
      { label: `${translations[lang].airplaneAnswer4}`,
        name: 'parentPlaneAskFlightAttendant' },
      { label: `${translations[lang].airplaneAnswer5}`,
        name: 'parentPlaneTranquilizerOrDrink' },
      { label: `${translations[lang].airplaneAnswer6}`, name: 'parentPlaneListenEnginesAndCrew' },
      { label: `${translations[lang].airplaneAnswer7}`,
        name: 'parentPlaneTalkAboutIssue' },
      { label: `${translations[lang].airplaneAnswer8}`, name: 'parentPlaneReadOrWrite' },
    ],
  },

  {
    Component: IntakeStepButton, buttonLabel: `${translations[lang].okLetsGo}`, children: <div>
      <ScaleExplanation labels={[ `${translations[lang].veryMuchDisagreement}`, `${translations[lang].aLittleDisagreement}`, `${translations[lang].notDisagreement}`, `${translations[lang].aLittleAgree}`, `${translations[lang].veryMuchInAgreement}` ]} />
    </div>, completed: intake => !!intake.checkedParentDescribeText,
    label:`${translations[lang].familyDescription}`,
    name: 'checkedParentDescribeText',
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentMakeSacrifices !== null,
    label: `${translations[lang].parentMakeSacrifices}`,
    name: 'parentMakeSacrifices',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentHasProblems !== null,
    label: `${translations[lang].parentHasProblems}`,
    name: 'parentHasProblems',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.wouldHelpRelatives !== null,
    label: `${translations[lang].wouldHelpRelatives}`,
    name: 'wouldHelpRelatives',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.shouldLiveLong !== null,
    label: `${translations[lang].shouldLiveLong}`,
    name: 'shouldLiveLong',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.agingParents !== null,
    label: `${translations[lang].agingParents}`,
    name: 'agingParents',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.shouldShareWithRelatives !== null,
    label: `${translations[lang].shouldShareWithRelatives}`,
    name: 'shouldShareWithRelatives',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.helpRelatives !== null,
    label: `${translations[lang].helpRelatives}`,
    name: 'helpRelatives',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.helpEconomically !== null,
    label: `${translations[lang].helpEconomically}`,
    name: 'helpEconomically',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.shouldBeEmbarrassed !== null,
    label: `${translations[lang].shouldBeEmbarrassed}`,
    name: 'shouldBeEmbarrassed',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.shouldPleaseParents !== null,
    label: `${translations[lang].shouldPleaseParents}`,
    name: 'shouldPleaseParents',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.familyShouldConsult !== null,
    label: `${translations[lang].familyShouldConsult}`,
    name: 'familyShouldConsult',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.canCountOnRelative !== null,
    label: `${translations[lang].canCountOnRelative}`,
    name: 'canCountOnRelative',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.shouldLiveInParentsHouse !== null,
    label: `${translations[lang].shouldLiveInParentsHouse}`,
    name: 'shouldLiveInParentsHouse',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.goalToHaveChildren !== null,
    label: `${translations[lang].goalToHaveChildren}`,
    name: 'goalToHaveChildren',
    scaleFrom: 1, scaleTo: 5,
  },
  {
    Component: IntakeStepButton, buttonLabel: `${translations[lang].okLetsGo}`, children: <div>
      <ScaleExplanation start={-1} labels={[ `${translations[lang].notImportant}`, `${translations[lang].aLittleImportant}`, `${translations[lang].somewhatImportant}`, `${translations[lang].veryImportant}`, `${translations[lang].extremelyImportant}` ]} />
    </div>, completed: intake => !!intake.checkedParentDescribeText,
    label: translations[lang].sympaticaInstructions,
    name: 'checkedParentDescribeText',
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentShareFeelings !== null,
    label: `${translations[lang].parentShareFeelings}`,
    name: 'parentShareFeelings',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentRespectOthers !== null,
    label: `${translations[lang].parentRespectOthers}`,
    name: 'parentRespectOthers',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAvoidConflict !== null,
    label: `${translations[lang].parentAvoidConflict}`,
    name: 'parentAvoidConflict',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentControlEmotions !== null,
    label: `${translations[lang].parentControlEmotions}`,
    name: 'parentControlEmotions',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentShowLoyalty !== null,
    label: `${translations[lang].parentShowLoyalty}`,
    name: 'parentShowLoyalty',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentObeyWishes !== null,
    label: `${translations[lang].parentObeyWishes}`,
    name: 'parentObeyWishes',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentShowAgreement !== null,
    label: `${translations[lang].parentShowAgreement}`,
    name: 'parentShowAgreement',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentGoodManners !== null,
    label: `${translations[lang].parentGoodManners}`,
    name: 'parentGoodManners',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentFeelComfortable !== null,
    label: `${translations[lang].parentFeelComfortable}`,
    name: 'parentFeelComfortable',
    scaleFrom: 0, scaleTo: 4,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.parentAvoidRudeness !== null,
    label: `${translations[lang].parentAvoidRudeness}`,
    name: 'parentAvoidRudeness',
    scaleFrom: 0, scaleTo: 4,
  },

  {
    Component: IntakeStepButton, buttonLabel: `${translations[lang].okLetsGo}`, children: <div>
      <div>{translations[lang].religiosityInstructions}</div>
      <ScaleExplanation labels={[ `${translations[lang].religiousScale1}`,  `${translations[lang].religiousScale2}`, `${translations[lang].religiousScale3}` ]} />
    </div>, completed: intake => !!intake.checkedParentDescribeText,
    label:`${translations[lang].religiousDescription}`,
    name: 'religiousDescription',
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ1 !== null,
    label: `${translations[lang].religousQ1}`,
    name: 'religousQ1',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ2 !== null,
    label: `${translations[lang].religousQ2}`,
    name: 'religousQ2',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ3 !== null,
    label: `${translations[lang].religousQ3}`,
    name: 'religousQ3',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ4 !== null,
    label: `${translations[lang].religousQ4}`,
    name: 'religousQ4',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ5 !== null,
    label: `${translations[lang].religousQ5}`,
    name: 'religousQ5',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ6 !== null,
    label: `${translations[lang].religousQ6}`,
    name: 'religousQ6',
    scaleFrom: 1, scaleTo: 3,
  },
  {
    Component: IntakeStepScale, completed: intake => intake.religousQ7 !== null,
    label: `${translations[lang].religousQ7}`,
    name: 'religousQ7',
    scaleFrom: 1, scaleTo: 3,
  },

  {
    Component: IntakeStepInputText, completed: intake => !!intake.childFavoriteToys,
    label: `${translations[lang].childFavoriteToys}`, name: 'childFavoriteToys', placeholder: '',
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childFavoriteGames,
    label: `${translations[lang].childFavoriteGames}`, name: 'childFavoriteGames', placeholder: '',
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childEnjoysMovies,
    label: `${translations[lang].childEnjoysMovies}`, name: 'childEnjoysMovies', placeholder: '',
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childPets,
    label: `${translations[lang].childPets}`, name: 'childPets', placeholder: '',
  },
  {
    Component: IntakeStepInputText, completed: intake => !!intake.childSportsOrExtracurricular,
    label: `${translations[lang].childSportsOrExtracurricular}`,
    name: 'childSportsOrExtracurricular', placeholder: '',
  },
]



export const intake1Steps = steps



// WEBPACK FOOTER //
// ./src/organisms/Intakes/intake1Steps.tsx